import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Pencil, Trash2, X, RefreshCw, Search, Plus, UserPlus, Share2, Users } from "lucide-react";
import { apiFetch, apiPatch, apiPost, apiDelete } from "./api/client";
import { UI } from "./designSystem";
import { getEffectiveRoles } from "./roleSimulator";

// Voltar dessas telas de administração deve ir pro painel de admin (/admin),
// não pro site.
const ADMIN_PATH = "/admin";

// PENDENTE = veio do autocadastro publico (/cadastro/crianca) e ainda
// ninguem da equipe revisou -- trocar pra "Ativo" no editar e a aprovacao.
const STATUS_OPTIONS = ["ATIVO", "INATIVO", "PENDENTE"];
const STATUS_LABELS = { ATIVO: "Ativo", INATIVO: "Inativo (saiu)", CONVERTIDO: "Virou membro", PENDENTE: "Pendente" };
const STATUS_COLORS = {
  ATIVO: { border: "rgba(74,222,128,0.45)", background: "rgba(34,197,94,0.14)", color: "#86EFAC" },
  INATIVO: { border: "rgba(148,163,184,0.4)", background: "rgba(148,163,184,0.12)", color: "#c7ced9" },
  CONVERTIDO: { border: "rgba(125,211,252,0.4)", background: "rgba(14,165,233,0.12)", color: "#7DD3FC" },
  PENDENTE: { border: "rgba(251,191,36,0.45)", background: "rgba(251,191,36,0.14)", color: "#FDE68A" }
};

const MONTH_NAMES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
];

// Faixas usadas pelo filtro de idade -- criancas pequenas, pre-adolescentes/
// adolescentes e quem ja esta na idade de virar membro (mesmo corte de 16
// anos usado na sugestao de "converter em membro" abaixo).
const AGE_FILTER_LABELS = {
  ATE_8: "Até 8 anos",
  DE_9_A_15: "9 a 15 anos",
  MAIS_16: "16 anos ou mais"
};

function matchesAgeFilter(age, ageFilter) {
  if (age === null) return false;
  if (ageFilter === "ATE_8") return age <= 8;
  if (ageFilter === "DE_9_A_15") return age >= 9 && age <= 15;
  if (ageFilter === "MAIS_16") return age >= 16;
  return true;
}

const EMPTY_FORM = {
  fullName: "",
  birthDate: "",
  address: "",
  addressComplement: "",
  neighborhood: "",
  city: "",
  state: "",
  zipCode: "",
  fatherName: "",
  fatherPhone: "",
  fatherEmail: "",
  motherName: "",
  motherPhone: "",
  motherEmail: "",
  guardianName: "",
  guardianPhone: "",
  guardianRelationship: "",
  livesWith: "",
  hasAllergies: false,
  allergyDetails: "",
  medications: "",
  imageAuthorization: true,
  importantNotes: "",
  staffNotes: "",
  status: "ATIVO",
  linkedUserId: ""
};

function dateForInput(value) {
  if (!value) return "";
  const iso = String(value);
  return iso.length >= 10 ? iso.slice(0, 10) : "";
}

// Le mes/dia direto da string ISO em vez de montar um Date e chamar
// getMonth() -- assim o fuso horario do navegador nao empurra a data pro
// dia anterior perto da meia-noite (mesmo padrao do AdminMembros).
function monthDayOf(value) {
  if (!value) return null;
  const iso = String(value);
  if (iso.length < 10) return null;
  const month = Number(iso.slice(5, 7));
  const day = Number(iso.slice(8, 10));
  if (!month || !day) return null;
  return { month, day };
}

function ageOf(value) {
  const birth = dateForInput(value);
  if (!birth) return null;
  const [y, m, d] = birth.split("-").map(Number);
  const today = new Date();
  let age = today.getFullYear() - y;
  const hadBirthdayThisYear = today.getMonth() + 1 > m || (today.getMonth() + 1 === m && today.getDate() >= d);
  if (!hadBirthdayThisYear) age -= 1;
  return age;
}

function formToBody(form) {
  return {
    ...form,
    birthDate: form.birthDate || null,
    fatherEmail: form.fatherEmail.trim() || null,
    motherEmail: form.motherEmail.trim() || null,
    linkedUserId: form.linkedUserId || null
  };
}

const pageStyle = {
  minHeight: "100vh",
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: UI.shell,
  color: UI.text,
  fontFamily: "'DM Sans', system-ui, sans-serif",
  padding: "28px 20px 60px"
};

const cardStyle = {
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.28)",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)"
};

const backLinkStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  color: "#9fb0cc",
  fontSize: 13,
  fontWeight: 700,
  textDecoration: "none",
  marginBottom: 18
};

const iconButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.28)",
  background: "rgba(255,255,255,0.03)",
  color: "#c7ced9",
  cursor: "pointer"
};

const modalBackdropStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(4,8,18,0.72)",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: 20,
  overflowY: "auto",
  zIndex: 50
};

const modalCardStyle = {
  width: "100%",
  maxWidth: 680,
  margin: "3vh auto",
  background: "linear-gradient(160deg, #0f1727, #131f34)",
  border: "1px solid rgba(120,152,210,0.32)",
  borderRadius: 16,
  padding: 22,
  boxShadow: "0 24px 60px rgba(0,0,0,0.5)"
};

const modalCloseButtonStyle = {
  position: "absolute",
  top: 14,
  right: 14,
  width: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "none",
  background: "rgba(255,255,255,0.06)",
  borderRadius: 8,
  color: "#9fb0cc",
  cursor: "pointer"
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "rgba(255,255,255,0.04)",
  color: "#e5e7eb",
  fontSize: 14,
  fontFamily: "inherit",
  // Sem isso o calendario nativo do input[type=date] renderia claro,
  // destoando do resto do app (mesmo ajuste feito no AdminMembros).
  colorScheme: "dark"
};

const textareaStyle = { ...inputStyle, resize: "vertical", minHeight: 64, lineHeight: 1.5 };

const selectStyle = { ...inputStyle };

const fieldLabelStyle = {
  display: "block",
  margin: "0 0 6px",
  fontSize: 12,
  fontWeight: 800,
  color: "#9fb0cc"
};

const sectionTitleStyle = {
  margin: "22px 0 10px",
  fontSize: 11,
  letterSpacing: 1.1,
  textTransform: "uppercase",
  fontWeight: 900,
  color: "#7DD3FC"
};

const primaryButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(125,211,252,0.4)",
  background: "rgba(14,165,233,0.18)",
  color: "#BAE6FD",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer"
};

const secondaryButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(148,163,184,0.32)",
  background: "transparent",
  color: "#c7ced9",
  fontSize: 13,
  fontWeight: 800,
  cursor: "pointer"
};

const dangerButtonStyle = {
  minHeight: 40,
  padding: "0 18px",
  borderRadius: 9,
  border: "1px solid rgba(248,113,113,0.4)",
  background: "rgba(248,113,113,0.14)",
  color: "#FCA5A5",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer"
};

function CenteredMessage({ children }) {
  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 480, margin: "18vh auto 0", textAlign: "center" }}>
        <p style={{ margin: 0, color: "#c7ced9", fontSize: 14, lineHeight: 1.6 }}>{children}</p>
        <Link to={ADMIN_PATH} style={{ ...backLinkStyle, marginTop: 18, justifyContent: "center" }}>
          <ArrowLeft size={14} /> Voltar
        </Link>
      </div>
    </div>
  );
}

function FieldRow({ children, columns = 2 }) {
  return <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: 12, marginBottom: 12 }}>{children}</div>;
}

function Field({ label, required, ...rest }) {
  return (
    <div>
      <label style={fieldLabelStyle}>
        {label}
        {required && <span style={{ color: "#f0a84e", fontWeight: 700 }}> *</span>}
      </label>
      <input style={inputStyle} {...rest} />
    </div>
  );
}

export default function AdminCriancas({ portalAccess } = {}) {
  const [status, setStatus] = useState("checking");
  const [children, setChildren] = useState([]);
  const [childrenLoading, setChildrenLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [birthdayMonth, setBirthdayMonth] = useState("");
  const [ageFilter, setAgeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("ATIVO");
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const [feedback, setFeedback] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [editingChild, setEditingChild] = useState(null); // null = criando novo
  const [form, setForm] = useState(EMPTY_FORM);
  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [convertTarget, setConvertTarget] = useState(null);
  const [convertingId, setConvertingId] = useState(null);
  const [successFeedback, setSuccessFeedback] = useState("");

  // Contas de app ainda sem pessoa vinculada (ver /children/linkable-users)
  // -- alimenta o seletor "Conta do app vinculada" no formulario, mesmo
  // padrao do Member em AdminMembros.jsx.
  const [linkableUsers, setLinkableUsers] = useState([]);
  // Sugestoes automaticas de "essa conta do app parece ser dessa crianca/
  // teen" por nome igual (ver /children/account-link-suggestions) -- e o
  // que resolve casos como o do Bernardo sem precisar abrir o cadastro.
  const [accountLinkSuggestions, setAccountLinkSuggestions] = useState([]);
  const [dismissedAccountSuggestions, setDismissedAccountSuggestions] = useState(() => new Set());
  const [linkingChildId, setLinkingChildId] = useState(null);

  useEffect(() => {
    // Quando chega via o portal (AdminMembrosPortal.jsx / AdminCriancasPortal.jsx),
    // o papel de quem esta logado ja foi checado uma vez la -- evita bater
    // /auth/me de novo aqui e reaparecer um "Carregando..." por cima do que
    // o portal ja resolveu.
    if (portalAccess !== undefined) {
      setStatus(portalAccess ? "ready" : "forbidden");
      return undefined;
    }
    let alive = true;
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((me) => {
        if (!alive) return;
        const effectiveMeRoles = getEffectiveRoles(me);
        const canAccess = effectiveMeRoles.includes("ADMIN") || effectiveMeRoles.includes("SECRETARIA");
        if (canAccess && !me.passwordMustChange) {
          setStatus("ready");
        } else {
          setStatus("forbidden");
        }
      })
      .catch(() => {
        if (alive) setStatus("forbidden");
      });
    return () => {
      alive = false;
    };
  }, [portalAccess]);

  const loadChildren = useCallback(async (searchTerm) => {
    setChildrenLoading(true);
    setFeedback("");
    try {
      const qs = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : "";
      const data = await apiFetch(`/children${qs}`, { method: "GET" });
      setChildren(Array.isArray(data) ? data : []);
    } catch (error) {
      setFeedback("Não foi possível carregar a lista agora.");
    } finally {
      setChildrenLoading(false);
    }
  }, []);

  useEffect(() => {
    if (status === "ready") loadChildren("");
  }, [status, loadChildren]);

  // Busca no servidor com um pequeno debounce -- evita bater na API a cada tecla.
  useEffect(() => {
    if (status !== "ready") return undefined;
    const timer = setTimeout(() => loadChildren(search.trim()), 320);
    return () => clearTimeout(timer);
  }, [search, status, loadChildren]);

  const loadLinkableUsers = useCallback(async () => {
    try {
      const data = await apiFetch("/children/linkable-users", { method: "GET" });
      setLinkableUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      setLinkableUsers([]);
    }
  }, []);

  const loadAccountLinkSuggestions = useCallback(async () => {
    try {
      const data = await apiFetch("/children/account-link-suggestions", { method: "GET" });
      setAccountLinkSuggestions(Array.isArray(data) ? data : []);
    } catch (error) {
      setAccountLinkSuggestions([]);
    }
  }, []);

  useEffect(() => {
    if (status === "ready") loadAccountLinkSuggestions();
  }, [status, loadAccountLinkSuggestions]);

  // Confirma uma sugestao de vinculo: PATCH na crianca/teen com o
  // linkedUserId sugerido. Mesmo racional de linkAccountSuggestion em
  // AdminMembros.jsx -- se o estado mudou desde que a sugestao foi
  // calculada, o backend recusa com 409 e so recarrega as sugestoes.
  const linkAccountSuggestion = async (suggestion) => {
    setFeedback("");
    setLinkingChildId(suggestion.child.id);
    try {
      const updated = await apiPatch(`/children/${encodeURIComponent(suggestion.child.id)}`, {
        body: { linkedUserId: suggestion.user.id }
      });
      setChildren((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      setAccountLinkSuggestions((prev) =>
        prev.filter((s) => s.child.id !== suggestion.child.id && s.user.id !== suggestion.user.id)
      );
    } catch (error) {
      setFeedback(error?.message || "Não foi possível vincular essa conta agora.");
      loadAccountLinkSuggestions();
    } finally {
      setLinkingChildId(null);
    }
  };

  const openCreateForm = () => {
    setEditingChild(null);
    setForm(EMPTY_FORM);
    setFormError("");
    loadLinkableUsers();
    setFormOpen(true);
  };

  const openEditForm = (child) => {
    setEditingChild(child);
    setForm({
      fullName: child.fullName || "",
      birthDate: dateForInput(child.birthDate),
      address: child.address || "",
      addressComplement: child.addressComplement || "",
      neighborhood: child.neighborhood || "",
      city: child.city || "",
      state: child.state || "",
      zipCode: child.zipCode || "",
      fatherName: child.fatherName || "",
      fatherPhone: child.fatherPhone || "",
      fatherEmail: child.fatherEmail || "",
      motherName: child.motherName || "",
      motherPhone: child.motherPhone || "",
      motherEmail: child.motherEmail || "",
      guardianName: child.guardianName || "",
      guardianPhone: child.guardianPhone || "",
      guardianRelationship: child.guardianRelationship || "",
      livesWith: child.livesWith || "",
      hasAllergies: Boolean(child.hasAllergies),
      allergyDetails: child.allergyDetails || "",
      medications: child.medications || "",
      imageAuthorization: child.imageAuthorization !== false,
      importantNotes: child.importantNotes || "",
      staffNotes: child.staffNotes || "",
      status: child.status || "ATIVO",
      linkedUserId: child.linkedUserId || ""
    });
    setFormError("");
    loadLinkableUsers();
    setFormOpen(true);
  };

  const closeForm = () => {
    if (formSaving) return;
    setFormOpen(false);
    setEditingChild(null);
    setFormError("");
  };

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const saveForm = async () => {
    if (!form.fullName.trim()) {
      setFormError("Nome completo é obrigatório.");
      return;
    }
    setFormSaving(true);
    setFormError("");
    try {
      const body = formToBody(form);
      if (editingChild) {
        const updated = await apiPatch(`/children/${encodeURIComponent(editingChild.id)}`, { body });
        setChildren((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
      } else {
        const created = await apiPost("/children", { body });
        setChildren((prev) => [...prev, created].sort((a, b) => a.fullName.localeCompare(b.fullName)));
      }
      setFormOpen(false);
      setEditingChild(null);
    } catch (error) {
      setFormError(error?.message || "Não foi possível salvar esse cadastro.");
    } finally {
      setFormSaving(false);
    }
  };

  const confirmDelete = async () => {
    const child = deleteTarget;
    if (!child) return;
    setDeletingId(child.id);
    setFeedback("");
    try {
      await apiDelete(`/children/${encodeURIComponent(child.id)}`);
      setChildren((prev) => prev.filter((item) => item.id !== child.id));
      setDeleteTarget(null);
    } catch (error) {
      setFeedback(error?.message || "Não foi possível excluir esse cadastro.");
      setDeleteTarget(null);
    } finally {
      setDeletingId(null);
    }
  };

  // Converte o cadastro de kids/teens num cadastro de membro (pensado pra
  // quando a pessoa completa 16 anos) -- cria o Member com o que ja temos
  // (nome, endereco, pai/mae) e marca esse registro como CONVERTIDO, sem
  // apagar o historico de alergia/autorizacao de imagem.
  const confirmConvert = async () => {
    const child = convertTarget;
    if (!child) return;
    setConvertingId(child.id);
    setFeedback("");
    try {
      const { member } = await apiPost(`/children/${encodeURIComponent(child.id)}/convert-to-member`);
      setChildren((prev) =>
        prev.map((item) =>
          item.id === child.id ? { ...item, status: "CONVERTIDO", convertedMemberId: member.id, convertedMember: member } : item
        )
      );
      setConvertTarget(null);
      setSuccessFeedback(`${child.fullName} agora está no cadastro de membros. A sugestão de família em /admin/membros deve reconhecer os pais automaticamente.`);
    } catch (error) {
      setFeedback(error?.message || "Não foi possível converter esse cadastro em membro.");
      setConvertTarget(null);
    } finally {
      setConvertingId(null);
    }
  };

  const statusFilteredChildren = statusFilter ? children.filter((child) => (child.status || "ATIVO") === statusFilter) : children;
  const ageFilteredChildren = ageFilter
    ? statusFilteredChildren.filter((child) => matchesAgeFilter(ageOf(child.birthDate), ageFilter))
    : statusFilteredChildren;
  const pendingChildrenCount = children.filter((child) => child.status === "PENDENTE").length;

  const copyShareLink = (path) => {
    const url = `${window.location.origin}${path}`;
    navigator.clipboard?.writeText(url).then(() => {
      setShareLinkCopied(true);
      setTimeout(() => setShareLinkCopied(false), 2500);
    });
  };
  const visibleChildren = birthdayMonth
    ? ageFilteredChildren
        .map((child) => ({ child, birth: monthDayOf(child.birthDate) }))
        .filter((entry) => entry.birth && entry.birth.month === Number(birthdayMonth))
        .sort((a, b) => a.birth.day - b.birth.day)
        .map((entry) => entry.child)
    : ageFilteredChildren;

  if (status === "checking") {
    return <CenteredMessage>Carregando...</CenteredMessage>;
  }

  if (status === "forbidden") {
    return <CenteredMessage>Esta página é restrita à secretaria e administradores. Entre com uma conta com esse papel para ver e gerenciar o cadastro de kids/teens.</CenteredMessage>;
  }

  return (
    <div style={pageStyle}>
      <div style={{ maxWidth: 980, margin: "0 auto" }}>
        <Link to={ADMIN_PATH} style={backLinkStyle}>
          <ArrowLeft size={14} /> Voltar
        </Link>

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
          <div>
            <p style={{ margin: "0 0 6px", fontSize: 11, letterSpacing: 1.35, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>Ministério infantil</p>
            <h1 style={{ margin: 0, fontSize: 26, fontWeight: 950, color: "#FFF" }}>Cadastro de kids e teens</h1>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => loadChildren(search.trim())}
              disabled={childrenLoading}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 38, padding: "0 14px", border: "1px solid rgba(125,211,252,0.38)", background: "rgba(14,165,233,0.12)", color: "#BAE6FD", fontSize: 12, fontWeight: 900, borderRadius: 8, cursor: childrenLoading ? "default" : "pointer", opacity: childrenLoading ? 0.7 : 1 }}
            >
              <RefreshCw size={13} />
              {childrenLoading ? "Atualizando..." : "Atualizar"}
            </button>
            <button
              onClick={() => copyShareLink("/cadastro/crianca")}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 38, padding: "0 14px", border: "1px solid rgba(251,191,36,0.4)", background: "rgba(251,191,36,0.12)", color: "#FDE68A", fontSize: 12, fontWeight: 900, borderRadius: 8, cursor: "pointer" }}
              title="Copia o link do formulário público de cadastro de kids/teens"
            >
              <Share2 size={13} />
              {shareLinkCopied ? "Link copiado!" : "Compartilhar link"}
            </button>
            <button
              onClick={openCreateForm}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 38, padding: "0 14px", border: "1px solid rgba(74,222,128,0.4)", background: "rgba(34,197,94,0.16)", color: "#86EFAC", fontSize: 12, fontWeight: 900, borderRadius: 8, cursor: "pointer" }}
            >
              <Plus size={14} />
              Nova criança/adolescente
            </button>
          </div>
        </div>
        <p style={{ margin: "0 0 14px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6, maxWidth: 640 }}>
          Cadastro de kids e teens do ministério infantil -- pais/responsáveis, saúde e autorização de imagem, num só lugar. Use "Compartilhar link" pra mandar o formulário público pra quem ainda não cadastrou -- o cadastro entra como "Pendente" até alguém da equipe confirmar.
        </p>
        {pendingChildrenCount > 0 && (
          <button
            type="button"
            onClick={() => setStatusFilter("PENDENTE")}
            style={{ display: "block", width: "100%", textAlign: "left", marginBottom: 16, padding: "10px 14px", borderRadius: 10, border: "1px solid rgba(251,191,36,0.35)", background: "rgba(251,191,36,0.1)", color: "#FDE68A", fontSize: 12.5, fontWeight: 800, cursor: "pointer" }}
          >
            {pendingChildrenCount} {pendingChildrenCount === 1 ? "cadastro pendente" : "cadastros pendentes"} de aprovação -- clique pra ver
          </button>
        )}

        {accountLinkSuggestions.some((_, index) => !dismissedAccountSuggestions.has(index)) && (
          <div style={{ ...cardStyle, marginBottom: 16, borderColor: "rgba(125,211,252,0.35)" }}>
            <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.1, textTransform: "uppercase", fontWeight: 900, color: "#7DD3FC" }}>
              Sugestões de vínculo de conta
            </p>
            <p style={{ margin: "0 0 14px", color: "#9fb0cc", fontSize: 12.5, lineHeight: 1.5 }}>
              Essas contas do app parecem ser de uma criança/adolescente que ainda não tem conta vinculada (mesmo nome). Confira e confirme -- é o caso, por exemplo, de um teen que já tem login próprio.
            </p>
            <div style={{ display: "grid", gap: 12 }}>
              {accountLinkSuggestions.map((suggestion, index) => {
                if (dismissedAccountSuggestions.has(index)) return null;
                return (
                  <div key={`${suggestion.child.id}:${suggestion.user.id}`} style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 12 }}>
                    <p style={{ margin: "0 0 4px", color: "#E5E7EB", fontSize: 13, fontWeight: 800 }}>
                      {suggestion.child.fullName} <span style={{ color: "#9fb0cc", fontWeight: 600 }}>→</span> {suggestion.user.name} <span style={{ color: "#9fb0cc", fontWeight: 600 }}>({suggestion.user.email})</span>
                    </p>
                    <p style={{ margin: "0 0 10px", color: "#7DD3FC", fontSize: 11, fontWeight: 700 }}>{suggestion.reason}</p>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        onClick={() => linkAccountSuggestion(suggestion)}
                        disabled={linkingChildId === suggestion.child.id}
                        style={{ ...primaryButtonStyle, minHeight: 34, padding: "0 14px", fontSize: 12, opacity: linkingChildId === suggestion.child.id ? 0.7 : 1 }}
                      >
                        {linkingChildId === suggestion.child.id ? "Vinculando..." : "Vincular"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDismissedAccountSuggestions((prev) => new Set(prev).add(index))}
                        style={{ ...secondaryButtonStyle, minHeight: 34, padding: "0 14px", fontSize: 12 }}
                      >
                        Ignorar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          <div style={{ position: "relative", maxWidth: 320, flex: "1 1 260px" }}>
            <Search size={14} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#6b7d99", pointerEvents: "none" }} />
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar por nome, responsável ou cidade"
              style={{ ...inputStyle, paddingLeft: 34 }}
            />
          </div>
          <div style={{ maxWidth: 200, flex: "1 1 160px" }}>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              style={selectStyle}
              title="Filtrar por status"
            >
              <option value="ATIVO">Ativos</option>
              <option value="">Todos</option>
              <option value="PENDENTE">Pendentes de aprovação</option>
              <option value="INATIVO">Inativos (saíram)</option>
              <option value="CONVERTIDO">Viraram membro</option>
            </select>
          </div>
          <div style={{ maxWidth: 220, flex: "1 1 180px" }}>
            <select
              value={birthdayMonth}
              onChange={(event) => setBirthdayMonth(event.target.value)}
              style={selectStyle}
              title="Filtrar aniversariantes do mês"
            >
              <option value="">Aniversariantes -- todos os meses</option>
              {MONTH_NAMES.map((name, index) => (
                <option key={name} value={String(index + 1)}>Aniversariantes de {name}</option>
              ))}
            </select>
          </div>
          <div style={{ maxWidth: 200, flex: "1 1 170px" }}>
            <select
              value={ageFilter}
              onChange={(event) => setAgeFilter(event.target.value)}
              style={selectStyle}
              title="Filtrar por faixa de idade"
            >
              <option value="">Todas as idades</option>
              <option value="ATE_8">Até 8 anos</option>
              <option value="DE_9_A_15">9 a 15 anos</option>
              <option value="MAIS_16">16 anos ou mais</option>
            </select>
          </div>
        </div>

        {feedback && (
          <p style={{ margin: "0 0 16px", color: "#FCA5A5", fontSize: 13, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.28)", borderRadius: 10, padding: "10px 12px" }}>
            {feedback}
          </p>
        )}

        {successFeedback && (
          <p style={{ margin: "0 0 16px", color: "#86EFAC", fontSize: 13, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.28)", borderRadius: 10, padding: "10px 12px" }}>
            {successFeedback}
          </p>
        )}

        <div style={cardStyle}>
          {visibleChildren.length === 0 && !childrenLoading && (
            <p style={{ margin: 0, color: "#9fb0cc", fontSize: 13 }}>
              {birthdayMonth
                ? `Ninguém faz aniversário em ${MONTH_NAMES[Number(birthdayMonth) - 1]}.`
                : ageFilter
                ? `Ninguém na faixa "${AGE_FILTER_LABELS[ageFilter]}" agora.`
                : search.trim()
                ? `Nenhum registro encontrado para "${search}".`
                : statusFilter && children.length > 0
                ? `Nenhum cadastro com o status "${STATUS_LABELS[statusFilter] || statusFilter}" agora -- troque o filtro pra "Todos" pra ver o restante.`
                : "Nenhuma criança/adolescente cadastrada ainda."}
            </p>
          )}

          <div style={{ display: "grid", gap: 4 }}>
            {visibleChildren.map((child) => {
              const statusStyle = STATUS_COLORS[child.status] || STATUS_COLORS.ATIVO;
              const age = ageOf(child.birthDate);
              return (
                <div
                  key={child.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr) auto auto",
                    gap: 14,
                    alignItems: "center",
                    padding: "14px 0",
                    borderTop: "1px solid rgba(255,255,255,0.08)",
                    opacity: deletingId === child.id ? 0.6 : 1
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, color: "#E5E7EB", fontSize: 14, fontWeight: 850 }}>
                      {child.fullName}
                      {age !== null && <span style={{ color: "#9fb0cc", fontWeight: 700 }}> · {age} anos</span>}
                      {age !== null && age >= 16 && child.status === "ATIVO" && (
                        <span style={{ color: "#f0a84e", fontWeight: 700 }}> · sugestão: converter em membro</span>
                      )}
                    </p>
                    <p style={{ margin: "3px 0 0", color: "#9fb0cc", fontSize: 12, overflowWrap: "anywhere" }}>
                      {[
                        birthdayMonth && monthDayOf(child.birthDate)
                          ? `Aniversário: ${String(monthDayOf(child.birthDate).day).padStart(2, "0")}/${String(monthDayOf(child.birthDate).month).padStart(2, "0")}`
                          : null,
                        child.motherName ? `Mãe: ${child.motherName}` : null,
                        child.fatherName ? `Pai: ${child.fatherName}` : null,
                        child.city
                      ].filter(Boolean).join(" · ") || "Sem contato de responsável cadastrado"}
                    </p>
                    {child.hasAllergies && (
                      <p style={{ margin: "3px 0 0", color: "#f0a84e", fontSize: 11, fontWeight: 700 }}>
                        Alergia: {child.allergyDetails || "sim (sem detalhes)"}
                      </p>
                    )}
                    {child.householdId && (
                      // So um badge/link -- confirmar, ver quem mais esta na familia e
                      // remover continua sendo feito em /admin/membros (onde a lista de
                      // sugestoes e o modal "ver familia" ja tratam crianca e membro
                      // juntos), pra nao duplicar essa tela toda aqui.
                      <p style={{ margin: "4px 0 0", fontSize: 11 }}>
                        <Link
                          to="/admin/membros"
                          title="Ver e gerenciar essa família em Membros"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            border: "1px solid rgba(240,168,78,0.35)",
                            background: "rgba(240,168,78,0.1)",
                            borderRadius: 999,
                            padding: "3px 9px 3px 8px",
                            color: "#f0a84e",
                            fontSize: 11,
                            fontWeight: 700,
                            textDecoration: "none",
                            minHeight: 24
                          }}
                        >
                          <Users size={11} /> Vinculada(o) a uma família
                        </Link>
                      </p>
                    )}
                  </div>
                  <span
                    style={{
                      padding: "6px 12px",
                      borderRadius: 999,
                      border: `1px solid ${statusStyle.border}`,
                      background: statusStyle.background,
                      color: statusStyle.color,
                      fontSize: 11,
                      fontWeight: 900,
                      whiteSpace: "nowrap"
                    }}
                  >
                    {STATUS_LABELS[child.status] || child.status}
                  </span>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flex: "none" }}>
                    {/* So aparece a partir dos 16 anos (mesmo criterio do badge de
                        sugestao acima) -- antes disso nao faz sentido oferecer a
                        conversao, entao o botao nem aparece pra nao confundir quem
                        esta cadastrando uma crianca pequena. */}
                    {child.status === "ATIVO" && age !== null && age >= 16 && (
                      <button
                        type="button"
                        onClick={() => setConvertTarget(child)}
                        title="Converter em cadastro de membro"
                        style={iconButtonStyle}
                        onMouseEnter={(event) => { event.currentTarget.style.color = "#7DD3FC"; }}
                        onMouseLeave={(event) => { event.currentTarget.style.color = "#8a96ab"; }}
                      >
                        <UserPlus size={15} />
                      </button>
                    )}
                    <button type="button" onClick={() => openEditForm(child)} title="Editar cadastro" style={iconButtonStyle}>
                      <Pencil size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(child)}
                      disabled={deletingId === child.id}
                      title="Excluir do cadastro"
                      style={iconButtonStyle}
                      onMouseEnter={(event) => { event.currentTarget.style.color = "#FCA5A5"; }}
                      onMouseLeave={(event) => { event.currentTarget.style.color = "#8a96ab"; }}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {formOpen && (
        <div style={modalBackdropStyle} onClick={closeForm}>
          <div style={{ ...modalCardStyle, position: "relative" }} onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={closeForm} style={modalCloseButtonStyle} disabled={formSaving}>
              <X size={16} />
            </button>
            <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.2, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>
              {editingChild ? "Editar cadastro" : "Novo cadastro"}
            </p>
            <h2 style={{ margin: "0 0 6px", fontSize: 19, fontWeight: 900, color: "#FFF" }}>
              {editingChild ? editingChild.fullName : "Cadastrar criança/adolescente"}
            </h2>

            {formError && (
              <p style={{ margin: "10px 0 0", color: "#FCA5A5", fontSize: 13, background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.28)", borderRadius: 10, padding: "10px 12px" }}>
                {formError}
              </p>
            )}

            <p style={sectionTitleStyle}>Dados da criança/adolescente</p>
            <FieldRow columns={1}>
              <Field label="Nome completo" required type="text" value={form.fullName} onChange={(e) => updateField("fullName", e.target.value)} />
            </FieldRow>
            <FieldRow>
              <Field label="Data de nascimento" type="date" value={form.birthDate} onChange={(e) => updateField("birthDate", e.target.value)} />
              <div>
                <label style={fieldLabelStyle}>Situação</label>
                <select style={selectStyle} value={form.status} onChange={(e) => updateField("status", e.target.value)}>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>{STATUS_LABELS[option]}</option>
                  ))}
                </select>
              </div>
            </FieldRow>
            <FieldRow columns={1}>
              <div>
                <label style={fieldLabelStyle}>Conta do app vinculada (quando a própria criança/teen já tem login)</label>
                <select style={selectStyle} value={form.linkedUserId} onChange={(e) => updateField("linkedUserId", e.target.value)}>
                  <option value="">Nenhuma</option>
                  {editingChild?.linkedUser && !linkableUsers.some((u) => u.id === editingChild.linkedUser.id) && (
                    <option value={editingChild.linkedUser.id}>{editingChild.linkedUser.name} ({editingChild.linkedUser.email})</option>
                  )}
                  {linkableUsers.map((user) => (
                    <option key={user.id} value={user.id}>{user.name} ({user.email || "sem e-mail"})</option>
                  ))}
                </select>
              </div>
            </FieldRow>

            <p style={sectionTitleStyle}>Endereço</p>
            <FieldRow>
              <Field label="Endereço" type="text" value={form.address} onChange={(e) => updateField("address", e.target.value)} />
              <Field label="Complemento" type="text" value={form.addressComplement} onChange={(e) => updateField("addressComplement", e.target.value)} />
            </FieldRow>
            <FieldRow columns={3}>
              <Field label="Bairro" type="text" value={form.neighborhood} onChange={(e) => updateField("neighborhood", e.target.value)} />
              <Field label="Cidade" type="text" value={form.city} onChange={(e) => updateField("city", e.target.value)} />
              <Field label="Estado" type="text" value={form.state} onChange={(e) => updateField("state", e.target.value)} />
            </FieldRow>
            <FieldRow columns={1}>
              <Field label="CEP" type="text" value={form.zipCode} onChange={(e) => updateField("zipCode", e.target.value)} />
            </FieldRow>

            <p style={sectionTitleStyle}>Pai</p>
            <FieldRow columns={3}>
              <Field label="Nome do pai" type="text" value={form.fatherName} onChange={(e) => updateField("fatherName", e.target.value)} />
              <Field label="Telefone" type="text" value={form.fatherPhone} onChange={(e) => updateField("fatherPhone", e.target.value)} />
              <Field label="E-mail" type="email" value={form.fatherEmail} onChange={(e) => updateField("fatherEmail", e.target.value)} />
            </FieldRow>

            <p style={sectionTitleStyle}>Mãe</p>
            <FieldRow columns={3}>
              <Field label="Nome da mãe" type="text" value={form.motherName} onChange={(e) => updateField("motherName", e.target.value)} />
              <Field label="Telefone" type="text" value={form.motherPhone} onChange={(e) => updateField("motherPhone", e.target.value)} />
              <Field label="E-mail" type="email" value={form.motherEmail} onChange={(e) => updateField("motherEmail", e.target.value)} />
            </FieldRow>

            <p style={sectionTitleStyle}>Outro responsável (opcional)</p>
            <FieldRow columns={3}>
              <Field label="Nome" type="text" value={form.guardianName} onChange={(e) => updateField("guardianName", e.target.value)} />
              <Field label="Telefone" type="text" value={form.guardianPhone} onChange={(e) => updateField("guardianPhone", e.target.value)} />
              <Field label="Parentesco" type="text" value={form.guardianRelationship} onChange={(e) => updateField("guardianRelationship", e.target.value)} />
            </FieldRow>
            <FieldRow columns={1}>
              <Field label="Com quem a criança reside" type="text" value={form.livesWith} onChange={(e) => updateField("livesWith", e.target.value)} />
            </FieldRow>

            <p style={sectionTitleStyle}>Saúde e autorizações</p>
            <FieldRow columns={1}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#c7ced9", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.hasAllergies}
                  onChange={(e) => updateField("hasAllergies", e.target.checked)}
                />
                Tem alergia (alimento, medicamento etc.)
              </label>
            </FieldRow>
            {form.hasAllergies && (
              <FieldRow columns={1}>
                <Field label="Quais alergias" type="text" value={form.allergyDetails} onChange={(e) => updateField("allergyDetails", e.target.value)} />
              </FieldRow>
            )}
            <FieldRow columns={1}>
              <Field label="Medicamentos que utiliza" type="text" value={form.medications} onChange={(e) => updateField("medications", e.target.value)} />
            </FieldRow>
            <FieldRow columns={1}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#c7ced9", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.imageAuthorization}
                  onChange={(e) => updateField("imageAuthorization", e.target.checked)}
                />
                Autorização de uso de imagem (ambiente da igreja)
              </label>
            </FieldRow>
            <FieldRow columns={1}>
              <div>
                <label style={fieldLabelStyle}>Informações importantes (dos pais)</label>
                <textarea style={textareaStyle} rows={3} value={form.importantNotes} onChange={(e) => updateField("importantNotes", e.target.value)} />
              </div>
            </FieldRow>
            <FieldRow columns={1}>
              <div>
                <label style={fieldLabelStyle}>Observações da equipe (tias/tios)</label>
                <textarea style={textareaStyle} rows={3} value={form.staffNotes} onChange={(e) => updateField("staffNotes", e.target.value)} />
              </div>
            </FieldRow>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
              <button type="button" onClick={closeForm} disabled={formSaving} style={secondaryButtonStyle}>
                Cancelar
              </button>
              <button type="button" onClick={saveForm} disabled={formSaving} style={{ ...primaryButtonStyle, opacity: formSaving ? 0.7 : 1 }}>
                {formSaving ? "Salvando..." : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {convertTarget && (
        <div style={modalBackdropStyle} onClick={() => !convertingId && setConvertTarget(null)}>
          <div style={{ ...modalCardStyle, maxWidth: 440, position: "relative" }} onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={() => setConvertTarget(null)} style={modalCloseButtonStyle} disabled={Boolean(convertingId)}>
              <X size={16} />
            </button>
            <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.2, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>Converter em membro</p>
            <h2 style={{ margin: "0 0 16px", fontSize: 19, fontWeight: 900, color: "#FFF" }}>{convertTarget.fullName}</h2>
            <p style={{ margin: "0 0 22px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6 }}>
              Cria um cadastro de membro pra {convertTarget.fullName} com nome, endereço e pai/mãe já preenchidos, e marca este cadastro de kids/teens
              como convertido (ele continua existindo, com o histórico de alergia e autorização de imagem, só sai da lista ativa). Use quando a pessoa
              completar 16 anos ou já tiver passado disso.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" onClick={() => setConvertTarget(null)} disabled={Boolean(convertingId)} style={secondaryButtonStyle}>
                Cancelar
              </button>
              <button type="button" onClick={confirmConvert} disabled={Boolean(convertingId)} style={{ ...primaryButtonStyle, opacity: convertingId ? 0.7 : 1 }}>
                {convertingId ? "Convertendo..." : "Converter em membro"}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div style={modalBackdropStyle} onClick={() => !deletingId && setDeleteTarget(null)}>
          <div style={{ ...modalCardStyle, maxWidth: 420, position: "relative" }} onClick={(event) => event.stopPropagation()}>
            <button type="button" onClick={() => setDeleteTarget(null)} style={modalCloseButtonStyle} disabled={Boolean(deletingId)}>
              <X size={16} />
            </button>
            <p style={{ margin: "0 0 4px", fontSize: 11, letterSpacing: 1.2, color: "#FCA5A5", textTransform: "uppercase", fontWeight: 900 }}>Excluir cadastro</p>
            <h2 style={{ margin: "0 0 16px", fontSize: 19, fontWeight: 900, color: "#FFF" }}>{deleteTarget.fullName}</h2>
            <p style={{ margin: "0 0 22px", color: "#9fb0cc", fontSize: 13, lineHeight: 1.6 }}>
              Remover esta pessoa do cadastro de kids/teens? Essa ação não pode ser desfeita.
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button type="button" onClick={() => setDeleteTarget(null)} disabled={Boolean(deletingId)} style={secondaryButtonStyle}>
                Cancelar
              </button>
              <button type="button" onClick={confirmDelete} disabled={Boolean(deletingId)} style={{ ...dangerButtonStyle, opacity: deletingId ? 0.7 : 1 }}>
                {deletingId ? "Excluindo..." : "Excluir definitivamente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
